import React, { useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { equipmentSummary } from "@/lib/equipment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { JobReviewDialog } from "@/components/JobReviewDialog";
import { EditJobDialog, JOB_TYPE_OPTIONS } from "@/components/EditJobDialog";

const TYPE_LABELS = Object.fromEntries(JOB_TYPE_OPTIONS);
const STATUS_LABELS = { tech_complete: "Pending review", complete: "Closed" };
const LABEL = "font-condensed uppercase tracking-wider text-[11px] font-semibold";
const astDate = iso => (iso ? new Date(iso).toLocaleDateString("sv-SE", { timeZone: "America/Barbados" }) : "");
const astDateTime = iso => (iso ? new Date(iso).toLocaleString("en-GB", {
  timeZone: "America/Barbados", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—");

function Filters({ filters, setFilters, customers }) {
  const set = key => value => setFilters(f => ({ ...f, [key]: value }));
  return (
    <div className="bf-card mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="completed-filters">
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Customer</span>
        <Select value={filters.customer} onValueChange={set("customer")}>
          <SelectTrigger className="h-9 mt-1 rounded-[10px] text-[13px]" data-testid="completed-filter-customer"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All customers</SelectItem>
            {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </label>
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Status</span>
        <Select value={filters.status} onValueChange={set("status")}>
          <SelectTrigger className="h-9 mt-1 rounded-[10px] text-[13px]" data-testid="completed-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Pending review and closed</SelectItem>
            <SelectItem value="tech_complete">Pending review</SelectItem>
            <SelectItem value="complete">Closed</SelectItem>
          </SelectContent>
        </Select>
      </label>
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Completed from</span>
        <Input type="date" className="mt-1 h-9 rounded-[10px] text-[13px]" value={filters.from}
          onChange={e => set("from")(e.target.value)} data-testid="completed-filter-from" />
      </label>
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Completed to</span>
        <Input type="date" className="mt-1 h-9 rounded-[10px] text-[13px]" value={filters.to}
          onChange={e => set("to")(e.target.value)} data-testid="completed-filter-to" />
      </label>
      <label className="block"><span className={LABEL} style={{ color: "#5B6472" }}>Ticket</span>
        <Input className="mt-1 h-9 rounded-[10px] text-[13px]" placeholder="Search ticket" value={filters.ticket}
          onChange={e => set("ticket")(e.target.value)} data-testid="completed-filter-ticket" />
      </label>
    </div>
  );
}

function JobRow({ job, lookups, onOpen, onEdit }) {
  const customer = lookups.customers.find(c => c.id === job.customer_id);
  const site = lookups.sites.find(s => s.id === job.site_id);
  const unit = lookups.equipment.find(e => e.id === job.equipment_id);
  const pending = job.status === "tech_complete";
  return (
    <tr className="border-t cursor-pointer hover:bg-[#F6F7F9]" style={{ borderColor: "#EFF1F4" }}
        onClick={() => onOpen(job)} data-testid={`completed-row-${job.ticket_number}`}>
      <td className="px-3 py-3 mono text-[12.5px] font-semibold">{job.ticket_number}</td>
      <td className="px-3 py-3 text-[13px]"><div className="font-semibold">{customer?.name || "—"}</div>
        <div style={{ color: "#5B6472" }}>{site?.name || "—"}</div></td>
      <td className="px-3 py-3 text-[12.5px]" data-testid={`completed-equipment-${job.ticket_number}`}>{job.visit_ticket ? `Maintenance visit · ${job.unit_count || 0} units` : unit ? equipmentSummary(unit) : "—"}</td>
      <td className="px-3 py-3 text-[12.5px]">{TYPE_LABELS[job.job_type] || job.job_type || "Repair"}</td>
      <td className="px-3 py-3 text-[12.5px]">{astDateTime(job.completed_at)}</td>
      <td className="px-3 py-3 mono text-[12.5px]" data-testid={`completed-hours-${job.ticket_number}`}>
        {((job.working_minutes || 0) / 60).toFixed(2)} h</td>
      <td className="px-3 py-3">
        <span className="bf-chip" style={pending ? { background: "#E0F2F5", color: "#0E7490" } : { background: "#E7F6EC", color: "#15803D" }}
              data-testid={`completed-status-${job.ticket_number}`}>{STATUS_LABELS[job.status]}</span>
      </td>
      <td className="px-3 py-3 text-right">
        {pending && (
          <Button variant="outline" className="h-8 text-[12px] bf-btn-ghost"
            onClick={event => { event.stopPropagation(); onEdit(job); }} data-testid={`completed-edit-${job.ticket_number}`}>
            Edit job
          </Button>
        )}
      </td>
    </tr>
  );
}

function matches(job, filters) {
  if (!["tech_complete", "complete"].includes(job.status)) return false;
  if (filters.status !== "all" && job.status !== filters.status) return false;
  if (filters.customer !== "all" && job.customer_id !== filters.customer) return false;
  const day = astDate(job.completed_at);
  if (filters.from && (!day || day < filters.from)) return false;
  if (filters.to && (!day || day > filters.to)) return false;
  return !filters.ticket || (job.ticket_number || "").toLowerCase().includes(filters.ticket.trim().toLowerCase());
}

function CancelledJobs() {
  const [rows, setRows] = useState([]);
  const load = () => client.get("/jobs/cancelled").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const restore = async job => {
    try {
      await client.post(`/jobs/${job.id}/restore`);
      toast.success(`${job.ticket_number} restored`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not restore job");
    }
  };
  return (
    <div className="mt-8" data-testid="cancelled-section">
      <div className="text-[16px] font-semibold" style={{ color: "#12161B" }}>Cancelled jobs</div>
      <div className="text-[12.5px]" style={{ color: "#5B6472", marginTop: "4px" }} data-testid="cancelled-count">
        {rows.length} cancelled · visible to Managers only · nothing was deleted
      </div>
      <div className="bf-card mt-3 p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="cancelled-table">
          <thead>
            <tr className={LABEL} style={{ color: "#5B6472" }}>
              {["Ticket", "Customer / site", "Cancelled by", "When", "Reason", ""].map(h => <th key={h} className="px-3 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(job => (
              <tr key={job.id} className="border-t" style={{ borderColor: "#EFF1F4" }} data-testid={`cancelled-row-${job.ticket_number}`}>
                <td className="px-3 py-3 mono text-[12.5px] font-semibold">{job.ticket_number}</td>
                <td className="px-3 py-3 text-[13px]"><div className="font-semibold">{job.customer || "—"}</div>
                  <div style={{ color: "#5B6472" }}>{job.site || "—"}</div></td>
                <td className="px-3 py-3 text-[12.5px]">{job.cancelled_by_name || job.cancelled_by}</td>
                <td className="px-3 py-3 text-[12.5px]">{astDateTime(job.cancelled_at)}</td>
                <td className="px-3 py-3 text-[12.5px]" data-testid={`cancelled-reason-${job.ticket_number}`}>{job.cancel_reason}</td>
                <td className="px-3 py-3 text-right">
                  <Button variant="outline" className="h-8 text-[12px] bf-btn-ghost" onClick={() => restore(job)}
                    data-testid={`restore-job-${job.ticket_number}`}>Restore</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-center text-[12.5px] py-8" style={{ color: "#8A93A1" }} data-testid="cancelled-empty">No cancelled jobs.</div>}
      </div>
    </div>
  );
}

export default function Completed() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [lookups, setLookups] = useState({ customers: [], sites: [], equipment: [], techs: [] });
  const [filters, setFilters] = useState({ customer: "all", status: "all", from: "", to: "", ticket: "" });
  const [reviewJob, setReviewJob] = useState(null);
  const [editJob, setEditJob] = useState(null);

  const load = async () => {
    const [j, c, s, e, t] = await Promise.all([
      client.get("/jobs"), client.get("/customers"), client.get("/sites"), client.get("/equipment"), client.get("/technicians"),
    ]);
    setJobs(j.data);
    setLookups({ customers: c.data, sites: s.data, equipment: e.data, techs: t.data });
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => jobs.filter(job => matches(job, filters))
    .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0)), [jobs, filters]);

  return (
    <div data-testid="completed-page">
      <Toaster richColors position="top-right" />
      <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Operations</div>
      <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Completed jobs</h1>
      <div className="text-[12.5px]" style={{ color: "#5B6472", marginTop: "4px" }} data-testid="completed-count">
        {rows.length} job{rows.length !== 1 ? "s" : ""} · click a row to review or open the report
      </div>
      <Filters filters={filters} setFilters={setFilters} customers={lookups.customers} />
      <div className="bf-card mt-4 p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="completed-table">
          <thead>
            <tr className={LABEL} style={{ color: "#5B6472" }}>
              {["Ticket", "Customer / site", "Equipment", "Job type", "Completed", "Working hours", "Status", ""].map(h => (
                <th key={h} className="px-3 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(job => <JobRow key={job.id} job={job} lookups={lookups} onOpen={setReviewJob} onEdit={setEditJob} />)}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-center text-[12.5px] py-10" style={{ color: "#8A93A1" }} data-testid="completed-empty">No completed jobs match these filters.</div>}
      </div>
      {user?.role === "manager" && <CancelledJobs />}
      <JobReviewDialog job={reviewJob} onClose={() => setReviewJob(null)} onDone={() => { setReviewJob(null); load(); }} />
      <EditJobDialog job={editJob} techs={lookups.techs} onClose={() => setEditJob(null)} onDone={() => { setEditJob(null); load(); }} />
    </div>
  );
}
